/**
 * HVP Cascade Controller - events and logging
 *
 * Internal event bus passthrough, the DOM CustomEvent dispatcher (document
 * target, hvp source) and the legacy jQuery events turnaround-variations
 * listens for.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeEventMethods = {
    /**
     * Subscribe to event
     */
    on(event, callback) {
        return this.eventBus.on(event, callback);
    },

    /**
     * Emit event
     */
    emit(event, data) {
        return this.eventBus.emit(event, {
            ...data,
            productId: this.productId,
            timestamp: Date.now()
        });
    },

    /**
     * Fire custom DOM event
     */
    fireCustomEvent(name, detail) {
        const event = new CustomEvent(name, {
            bubbles: true,
            detail: {
                ...detail,
                productId: this.productId,
                source: 'hvp'
            }
        });

        document.dispatchEvent(event);
    },

    /**
     * Fire legacy jQuery event for turnaround-variations compatibility
     *
     * The turnaround-variations plugin listens for 'haru_progressive_ready'
     * to know when HVP has finished initialization and dropdown options are ready.
     *
     * We fire multiple times with delays to handle race conditions where
     * turnaround might not have set up its listener yet.
     */
    fireLegacyReadyEvent() {
        if (typeof jQuery === 'undefined') {
            window.hvpData && window.hvpData.debug && console.warn('[HVP DEBUG] jQuery not available - cannot fire legacy events');
            return;
        }

        // Build attribute list like the old plugin did
        const attributeNames = this.attributeOrder.map(attr => 'attribute_' + attr);

        const eventData = {
            updatedAttributes: attributeNames,
            dropdownCount: this.dropdowns.size,
            variationCount: this.variationCount,
            productId: this.productId,
            source: 'hvp'
        };

        // Fire immediately
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Firing legacy haru_progressive_ready event (immediate)', eventData);
        jQuery(document).trigger('haru_progressive_ready', eventData);

        // Fire again after short delays to handle race conditions
        // Turnaround might set up its listener slightly after HVP initializes
        setTimeout(() => {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Firing legacy haru_progressive_ready event (50ms delay)');
            jQuery(document).trigger('haru_progressive_ready', eventData);
        }, 50);

        setTimeout(() => {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Firing legacy haru_progressive_ready event (200ms delay)');
            jQuery(document).trigger('haru_progressive_ready', eventData);
        }, 200);
    },

    /**
     * Fire legacy jQuery event when dropdown options are updated
     *
     * The turnaround-variations plugin listens for 'haru_progressive_updated'
     * to know when to re-render its UI boxes after cascading updates.
     *
     * @param {Array<string>} updatedAttributes - List of attribute names that were updated
     */
    fireLegacyUpdatedEvent(updatedAttributes) {
        if (typeof jQuery !== 'undefined') {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Firing legacy haru_progressive_updated event:', updatedAttributes);
            jQuery(document).trigger('haru_progressive_updated', {
                updatedAttributes: updatedAttributes,
                productId: this.productId,
                selections: { ...this.selections },
                source: 'hvp'
            });
        }
    },

    /**
     * Log message (debug mode only)
     */
    log(...args) {
        if (this.debug) {
            window.hvpData && window.hvpData.debug && console.log(
                '%c[HVP Controller]',
                'background: #6366f1; color: #fff; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
                ...args
            );
        }
    },

    /**
     * Set form loading state
     */
    setFormLoading(loading) {
        if (loading) {
            this.form.classList.add('hvp-loading');
        } else {
            this.form.classList.remove('hvp-loading');
        }
    },

    /**
     * Show error message
     */
    showError(message) {
        // TODO: Implement user-friendly error display
        console.error('[HVP]', message);
    },
};

export default CascadeEventMethods;
